// File: frontend/src/pages/SupportedCountriesV2/SupportedCountriesV2.js

import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import MapComponent from '../../components/Dashboard/MapComponent';
import mapData from '../../data/mapbox_data_for_dashboard.json';
import { FiGlobe, FiClock, FiServer } from 'react-icons/fi';
import styles from './SupportedCountriesV2.module.css';

const SupportedCountriesV2 = () => {
  const { t } = useLanguage();
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('allegory_intelligence');

  const handleStyleLoad = (loaded) => {
    setStyleLoaded(loaded);
  };

  const categories = [
    {
      id: 'allegory_intelligence',
      name: mapData.categories.allegory_intelligence.name,
      description: t('supported_countries.categories.intelligence_desc', 'Countries where Allegory Intelligence is available in local languages')
    },
    {
      id: 'allegory_app',
      name: mapData.categories.allegory_app.name,
      description: t('supported_countries.categories.app_desc', 'Regions where the Allegory mobile app is available for download')
    },
    {
      id: 'cloud_data_centers',
      name: mapData.categories.cloud_data_centers.name,
      description: t('supported_countries.categories.cloud_desc', 'Global cloud infrastructure powering our services')
    }
  ];

  const stats = [
    {
      icon: <FiGlobe />,
      value: `${mapData.supported_countries.countries.length}+`,
      label: t('countries_supported')
    },
    {
      icon: <FiClock />,
      value: '24/7',
      label: t('global_coverage')
    },
    {
      icon: <FiServer />,
      value: '99.9%',
      label: t('service_uptime')
    }
  ];

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <div className={styles.heroSection}>
          <h1 className={styles.title}>{t('supported_countries_title', 'Supported Countries')}</h1>
          <div className={styles.titleUnderline}></div>
          <p className={styles.description}>
            {t('supported_countries_description', 'Explore where Allegory services are available around the world. Our global infrastructure ensures reliable access to our platform across multiple regions.')}
          </p>
        </div>

        <section className={styles.categorySection}>
          <div className={styles.categoryTabs}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`${styles.categoryTab} ${activeCategory === category.id ? styles.activeTab : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
          
          <div className={styles.categoryDescription}>
            {categories.find(cat => cat.id === activeCategory)?.description}
          </div>
        </section>

        <section className={styles.mapSection}>
          <div className={styles.mapContainer}>
            {!styleLoaded && (
              <div className={styles.mapLoading}>
                <div className={styles.loadingSpinner}></div>
                <span>{t('loading_map', 'Loading map...')}</span>
              </div>
            )}
            <MapComponent 
              data={mapData.categories[activeCategory]}
              category={activeCategory}
              onStyleLoad={handleStyleLoad}
            />
          </div>
        </section>

        <section className={styles.statsSection}>
          {stats.map((stat, index) => (
            <div key={index} className={styles.statCard}>
              <div className={styles.statIcon}>{stat.icon}</div>
              <h3 className={styles.statValue}>{stat.value}</h3>
              <p className={styles.statLabel}>{stat.label}</p>
            </div>
          ))}
        </section>

        <section className={styles.countryListSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('supported_countries.country_list', 'Supported Countries List')}</h2>
            <div className={styles.underline}></div>
          </div>
          
          <div className={styles.countryGrid}>
            {mapData.supported_countries.countries.map((country, index) => (
              <div key={index} className={styles.countryItem}>
                <span className={styles.countryName}>{country.name}</span>
                <span className={styles.countryCode}>{country.code}</span>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.infoSection}>
          <div className={styles.infoCard}>
            <h3>{t('supported_countries.expansion_title', 'Expanding Our Reach')}</h3>
            <p>{t('supported_countries.expansion_desc', 'We\'re continuously expanding our services to new regions. If your country isn\'t listed, sign up to be notified when we launch in your area.')}</p>
            <a href="/sign-up" className={styles.infoButton}>
              {t('supported_countries.notify_me', 'Notify Me of Launch')}
            </a>
          </div>
          
          <div className={styles.infoCard}>
            <h3>{t('supported_countries.questions_title', 'Have Questions?')}</h3>
            <p>{t('supported_countries.questions_desc', 'Contact our team to learn more about our global services and availability in your region.')}</p>
            <a href="/contact" className={styles.infoButton}>
              {t('supported_countries.contact_us', 'Contact Us')}
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SupportedCountriesV2;